// ./src/components/features/Travels/TravelWaypointsDrawer/TravelWaypointsDrawer.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Stack,
  Group,
  Text,
  Box,
  Divider,
  Paper,
  Loader,
  Center,
  ScrollArea,
  Image
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { Expense, ExpenseInput } from "@/utils/api";
import { useAppEnvironment, useUserProfileContext } from "@/contexts";
import { useTravels, useExpenses } from "@/hooks";
import { AppButton, AppDrawer, AppIcon, EmptyState } from "@/components/generic";
import { EXPENSE_TYPES } from "@/config";

import { TravelStatusBadge } from "../TravelStatusBadge";
import { WaypointEditModal } from "./WaypointEditModal";
import { WaypointsList, GroupedExpenses, ExpandedSections } from "./WaypointsList";
import { WaypointsMapView } from "./WaypointsMapView";
import { MobileMapOverlay } from "./MobileMapOverlay";
import { getExpensesWithCoordinates } from "./waypointsMapUtils";

// ============================================
// Types
// ============================================

export interface TravelWaypointsDrawerProps {
  /** Whether the drawer is open */
  opened: boolean;

  /** Function to close the drawer */
  onClose: () => void;

  /** Travel ID to load */
  travelId: string | null;
}

// ============================================
// Helper Functions
// ============================================

const formatDateTime = (dateString: string | undefined): string => {
  if (!dateString) return "—";
  return dayjs(dateString).format("MMM DD, YYYY HH:mm");
};

const groupExpensesByType = (expenses: Expense[]): GroupedExpenses => {
  return {
    waypoints: expenses.filter((e) => e.expenseType === EXPENSE_TYPES.TRAVEL_POINT),
    refuels: expenses.filter((e) => e.expenseType === EXPENSE_TYPES.REFUEL),
    expenses: expenses.filter((e) => e.expenseType === EXPENSE_TYPES.EXPENSE),
    checkpoints: expenses.filter((e) => e.expenseType === EXPENSE_TYPES.CHECKPOINT),
    revenues: expenses.filter((e) => e.expenseType === EXPENSE_TYPES.REVENUE),
  };
};

const getInitialExpandedSections = (): ExpandedSections => ({
  waypoints: true,
  refuels: true,
  expenses: true,
  checkpoints: false,
  revenues: true,
});

// ============================================
// Travel Summary Component
// ============================================

interface TravelSummaryProps {
  travel: any;
  distanceLabel: string;
  t: (key: string) => string;
}

const TravelSummary: React.FC<TravelSummaryProps> = ({ travel, distanceLabel, t }) => (
  <Paper p="md" withBorder>
    <Stack gap="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="lg" fw={600}>
            {travel.car?.label || t("travels:labels.unknownVehicle")}
          </Text>
          <Text size="sm" c="dimmed">
            {formatDateTime(travel.firstDttm)}
            {travel.lastDttm && ` → ${formatDateTime(travel.lastDttm)}`}
          </Text>
        </Stack>
        <TravelStatusBadge status={travel.status} />
      </Group>

      {/* Purpose */}
      {travel.purpose && (
        <Group gap="xs">
          <AppIcon iconName="Target" size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm">{travel.purpose}</Text>
        </Group>
      )}

      {/* Distance */}
      {travel.distance !== undefined && travel.distance !== null && (
        <Group gap="xs">
          <AppIcon iconName="Route" size={16} color="var(--mantine-color-gray-6)" />
          <Text size="sm">
            {travel.distance} {t(`common:${distanceLabel}`)}
          </Text>
        </Group>
      )}
    </Stack>
  </Paper>
);

// ============================================
// Route Map Image Component
// ============================================

interface RouteMapImageProps {
  fileUrl: string;
  gpsDistance?: number | null;
  t: (key: string, options?: Record<string, unknown>) => string;
  distanceLabel: string;
}

const RouteMapImage: React.FC<RouteMapImageProps> = ({
  fileUrl,
  gpsDistance,
  t,
  distanceLabel,
}) => (
  <Paper p={0} withBorder radius="md" style={{ overflow: "hidden" }}>
    <Image
      src={fileUrl}
      alt={t("travels:waypoints.routeMap.alt", { defaultValue: "Trip route map" })}
      radius={0}
      fit="cover"
      fallbackSrc={undefined}
      style={{ display: "block" }}
    />
    {gpsDistance != null && gpsDistance > 0 && (
      <Group gap="xs" px="sm" py={6} bg="neutral.0">
        <AppIcon
          iconName="Route"
          size={14}
          color="var(--mantine-color-brand-6)"
        />
        <Text size="xs" fw={600} c="brand.6">
          {t("travels:waypoints.routeMap.gpsDistance", {
            defaultValue: "GPS distance: {{distance}} {{unit}}",
            distance: gpsDistance.toFixed(1),
            unit: t(`common:${distanceLabel}`),
          })}
        </Text>
      </Group>
    )}
  </Paper>
);

// ============================================
// Main Component
// ============================================

const TravelWaypointsDrawerComponent: React.FC<TravelWaypointsDrawerProps> = ({
  opened,
  onClose,
  travelId,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort, volumeLabelShort, currency } = useUserProfileContext();

  // Responsive breakpoint
  const { isMobile } = useAppEnvironment();

  // Travel data (lite query — no waypoints, just travel summary)
  const {
    travel,
    loading: loadingTravel,
    get: getTravel,
  } = useTravels({ autoLoad: false });

  // All expenses linked to this travel (waypoints + refuels + expenses + checkpoints + revenues)
  const {
    expenses,
    loading: loadingExpenses,
    list: listExpenses,
    update: updateExpense,
    isUpdating,
  } = useExpenses({ autoLoad: false });

  // UI State
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>(
    () => getInitialExpandedSections()
  );
  const [editingWaypoint, setEditingWaypoint] = useState<Expense | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [hoveredWaypointId, setHoveredWaypointId] = useState<string | null>(null);

  // Load travel (without waypoints) and all linked expenses when drawer opens
  useEffect(() => {
    if (opened && travelId) {
      // Lite get — no waypoints (they come from listExpenses)
      getTravel(travelId, { includeWaypoints: false });
      // Fetch ALL expense types linked to this travel
      listExpenses(
        {
          travelId: [travelId],
        },
        { page: 1, pageSize: 100 },
        [{ name: "whenDone", order: "ASC" }],
        true
      );
    }
  }, [opened, travelId, getTravel, listExpenses]);

  // Reset state when drawer closes
  useEffect(() => {
    if (!opened) {
      setEditingWaypoint(null);
      setExpandedSections(getInitialExpandedSections());
      setShowMap(false);
      setHoveredWaypointId(null);
    }
  }, [opened]);

  // Group expenses by type
  const groupedExpenses = useMemo(() => {
    return groupExpensesByType(expenses);
  }, [expenses]);

  // Check if we have any waypoints with coordinates for the map
  const hasWaypointsWithCoordinates = useMemo(() => {
    return getExpensesWithCoordinates(expenses).length > 0;
  }, [expenses]);

  // Toggle section
  const toggleSection = useCallback((section: keyof ExpandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  // Handle edit waypoint
  const handleEditWaypoint = useCallback((waypoint: Expense) => {
    setEditingWaypoint(waypoint);
  }, []);

  // Handle save waypoint
  const handleSaveWaypoint = useCallback(
    async (id: string, data: ExpenseInput) => {
      const result = await updateExpense(id, data);

      if (result.didSucceed()) {
        setEditingWaypoint(null);
        // Refresh expenses list
        if (travelId) {
          listExpenses(
            {
              travelId: [travelId],
            },
            { page: 1, pageSize: 100 },
            [{ name: "whenDone", order: "ASC" }],
            true
          );
        }
      }

      return result;
    },
    [updateExpense, travelId, listExpenses]
  );

  // Handle close edit modal
  const handleCloseEditModal = useCallback(() => {
    setEditingWaypoint(null);
  }, []);

  // Handle toggle map view
  const handleToggleMap = useCallback(() => {
    setShowMap((prev) => !prev);
  }, []);

  // Handle close mobile map
  const handleCloseMobileMap = useCallback(() => {
    setShowMap(false);
  }, []);

  // Handle waypoint hover (from list or map)
  const handleWaypointHover = useCallback((waypointId: string | null) => {
    setHoveredWaypointId(waypointId);
  }, []);

  // Handle marker click on map (desktop - just highlight, mobile handled by overlay)
  const handleMarkerClick = useCallback((waypoint: Expense) => {
    // On desktop, we could scroll to the waypoint in the list
    // For now, just highlight it
    setHoveredWaypointId(waypoint.id || null);
  }, []);

  // Loading state
  const isLoading = loadingTravel || loadingExpenses;

  // Determine drawer size based on map visibility
  const drawerSize = showMap && !isMobile ? "100%" : "lg";

  // ============================================
  // Render
  // ============================================

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={t("travels:waypoints.title")}
        position="right"
        size={drawerSize}
        actionType="close"
      >
        {isLoading ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : !travel ? (
          <EmptyState
            display={true}
            title={t("travels:waypoints.notFound.title")}
            description={t("travels:waypoints.notFound.description")}
            iconName="Route"
          />
        ) : (
          <Box h="100%">
            {/* Desktop: Split view when map is shown */}
            {showMap && !isMobile ? (
              <Group
                align="stretch"
                gap={0}
                wrap="nowrap"
                h="100%"
                style={{ overflow: "hidden" }}
              >
                {/* Left panel: List */}
                <Box
                  w={400}
                  miw={350}
                  style={{
                    borderRight: "1px solid var(--mantine-color-gray-3)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <ScrollArea
                    h="100%"
                    px="md"
                    py="md"
                    offsetScrollbars
                  >
                    <Stack gap="md">
                      {/* Travel Summary */}
                      <TravelSummary
                        travel={travel}
                        distanceLabel={distanceLabelShort}
                        t={t}
                      />

                      {/* Route Map Image (completed trips with tracking) */}
                      {/* {travel.routeUploadedFile?.fileUrl && (
                        <RouteMapImage
                          fileUrl={travel.routeUploadedFile.fileUrl}
                          gpsDistance={travel.gpsDistance}
                          t={t}
                          distanceLabel={distanceLabelShort}
                        />
                      )} */}

                      {/* Back to list button */}
                      <AppButton
                        variant="secondary"
                        iconName="ArrowLeft"
                        onClick={handleToggleMap}
                        size="sm"
                      >
                        {t("travels:waypoints.map.hideMap")}
                      </AppButton>

                      <Divider />

                      {/* Waypoints List */}
                      <WaypointsList
                        groupedExpenses={groupedExpenses}
                        expandedSections={expandedSections}
                        onToggleSection={toggleSection}
                        hoveredWaypointId={hoveredWaypointId}
                        onWaypointHover={handleWaypointHover}
                        onEditWaypoint={handleEditWaypoint}
                        distanceLabel={distanceLabelShort}
                        volumeLabel={volumeLabelShort}
                        currencySymbol={currency}
                      />
                    </Stack>
                  </ScrollArea>
                </Box>

                {/* Right panel: Map */}
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <WaypointsMapView
                    expenses={expenses}
                    hoveredWaypointId={hoveredWaypointId}
                    onMarkerHover={handleWaypointHover}
                    onMarkerClick={handleMarkerClick}
                    height="100%"
                    distanceLabel={distanceLabelShort}
                    encodedPolyline={travel.encodedPolyline}
                  />
                </Box>
              </Group>
            ) : (
              /* Normal view: List only */
              <Stack gap="md">
                {/* Travel Summary */}
                <TravelSummary
                  travel={travel}
                  distanceLabel={distanceLabelShort}
                  t={t}
                />

                {/* Route Map Image (completed trips with tracking) */}
                {travel.routeUploadedFile?.fileUrl && (
                  <RouteMapImage
                    fileUrl={travel.routeUploadedFile.fileUrl}
                    gpsDistance={travel.gpsDistance}
                    t={t}
                    distanceLabel={distanceLabelShort}
                  />
                )}

                {/* Show on Map button */}
                {hasWaypointsWithCoordinates && (
                  <AppButton
                    variant="secondary"
                    iconName="Map"
                    onClick={handleToggleMap}
                    fullWidth
                  >
                    {t("travels:waypoints.map.showOnMap")}
                  </AppButton>
                )}

                <Divider />

                {/* Waypoints List */}
                <WaypointsList
                  groupedExpenses={groupedExpenses}
                  expandedSections={expandedSections}
                  onToggleSection={toggleSection}
                  hoveredWaypointId={hoveredWaypointId}
                  onWaypointHover={handleWaypointHover}
                  onEditWaypoint={handleEditWaypoint}
                  distanceLabel={distanceLabelShort}
                  volumeLabel={volumeLabelShort}
                  currencySymbol={currency}
                />
              </Stack>
            )}
          </Box>
        )}
      </AppDrawer>

      {/* Mobile Map Overlay */}
      <MobileMapOverlay
        opened={showMap && !!isMobile}
        onClose={handleCloseMobileMap}
        expenses={expenses}
        waypoints={groupedExpenses.waypoints}
        distanceLabel={distanceLabelShort}
        volumeLabel={volumeLabelShort}
        currencySymbol={currency}
        onEditWaypoint={handleEditWaypoint}
        encodedPolyline={travel?.encodedPolyline}
      />

      {/* Waypoint Edit Modal */}
      <WaypointEditModal
        opened={!!editingWaypoint}
        onClose={handleCloseEditModal}
        waypoint={editingWaypoint}
        onSave={handleSaveWaypoint}
        loading={isUpdating}
      />
    </>
  );
};

export const TravelWaypointsDrawer = React.memo(TravelWaypointsDrawerComponent);